// A message's BlockNote body, read-only, for messages with structure (M4-03: the daily brief in
// `#brief` is markdown the API stores as blocks: a heading, bold labels, list items). Its
// `body_text` is one plain line per block, so headings, bullets and bold would be lost; these
// messages render their blocks instead. Plain paragraphs keep the text rendering (mentions
// highlighted there). Only what `markdownToBlocks` (@companyos/domain-work/chat) writes is drawn:
// headings, bullet and numbered items, quotes, code, paragraphs; bold, italic, strike, code and
// links inline. Anything else falls back to its text.
import { Button, cn } from "@companyos/ui";
import { useRouter } from "@tanstack/react-router";
import { MonitorPlayIcon } from "lucide-react";
import { Fragment, type ReactNode } from "react";
import { type LiveLink, liveLinkSearch, parseLiveLink } from "../live/live-link";

type Styles = { bold?: boolean; italic?: boolean; strike?: boolean; code?: boolean };
type Inline = {
  type?: string;
  text?: string;
  styles?: Styles;
  href?: string;
  content?: Inline[];
  props?: { label?: string; name?: string; id?: string };
};
/** CHAT-DM: draws an @mention (its name and, when the block carries one, its member id). */
type MentionRender = (name: string, memberId: string | undefined) => ReactNode;
type Block = { type?: string; props?: { level?: number }; content?: unknown; children?: unknown };

const STRUCTURED = new Set(["heading", "bulletListItem", "numberedListItem", "quote", "codeBlock"]);

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** Whether the body has anything plain text would lose (a heading, a list, a styled run). */
export function hasStructure(body: unknown): boolean {
  if (!Array.isArray(body)) return false;
  return body.some((b) => {
    if (!isObj(b)) return false;
    if (STRUCTURED.has(String(b.type))) return true;
    return (
      Array.isArray(b.content) &&
      b.content.some(
        (n) =>
          isObj(n) &&
          (n.type === "link" ||
            (isObj(n.styles) && Object.values(n.styles).some((x) => x === true))),
      )
    );
  });
}

/** A mention's membership id from its composer id ("person:<id>" / "de:<id>"). */
const memberOfRef = (ref: string | undefined) =>
  ref?.includes(":") ? ref.slice(ref.indexOf(":") + 1) : ref;

/**
 * CHAT-DM: the member ids a body's mentions carry, by the name they show (a plain-text message
 * keeps them only in its blocks). A name two mentions give different ids maps to "" (ambiguous:
 * shown plain, never resolved by name).
 */
export function mentionIdsOf(body: unknown): Map<string, string> {
  const out = new Map<string, string>();
  const clash = new Set<string>();
  const walk = (nodes: unknown) => {
    if (!Array.isArray(nodes)) return;
    for (const raw of nodes) {
      if (!isObj(raw)) continue;
      const n = raw as Inline & { children?: unknown };
      if (n.type === "mention") {
        const name = n.props?.label ?? n.props?.name ?? "";
        const id = memberOfRef(n.props?.id);
        if (name && id) {
          if (out.has(name) && out.get(name) !== id) clash.add(name);
          else out.set(name, id);
        }
      }
      walk(n.content);
      walk(n.children);
    }
  };
  walk(body);
  for (const name of clash) out.set(name, "");
  return out;
}

const appOrigin = () =>
  typeof window !== "undefined" ? window.location.origin : "http://localhost";

/** A link's text (its runs, joined). */
function plainText(nodes: unknown): string {
  if (typeof nodes === "string") return nodes;
  if (!Array.isArray(nodes)) return "";
  return nodes
    .map((n) => (isObj(n) ? `${(n as Inline).text ?? ""}${plainText((n as Inline).content)}` : ""))
    .join("");
}

/** CANVAS-BLOCKS: "Open in Live" for a Live deep link, carrying the link's label. */
function OpenInLive({ link, label }: { link: LiveLink; label: string }) {
  // Outside the app's router (a bare render): a plain page load.
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | undefined;
  const search = liveLinkSearch(link);
  const href = `/c/${encodeURIComponent(link.slug)}/live?${new URLSearchParams(search)}`;
  const text = label.trim() || "Open in Live";
  return (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      data-slot="open-in-live"
      data-view={link.view}
      aria-label={`${text} · Open in Live`}
      className="mx-0.5 align-middle"
      onClick={() => {
        if (router)
          void router.navigate({
            to: "/c/$slug/live",
            params: { slug: link.slug },
            search: search as never,
          });
        else window.location.assign(href);
      }}
    >
      <MonitorPlayIcon aria-hidden="true" />
      <span>{text}</span>
      {label.trim() ? <span className="text-xs text-muted-foreground">Open in Live</span> : null}
    </Button>
  );
}

function inline(nodes: unknown, key = "", mention?: MentionRender): ReactNode[] {
  if (typeof nodes === "string") return [nodes];
  if (!Array.isArray(nodes)) return [];
  return nodes.map((raw, i) => {
    const n = (isObj(raw) ? raw : {}) as Inline;
    const k = `${key}${i}`;
    // CANVAS-BLOCKS: an agent's Live deep link is a button that opens Live in the app.
    const liveLink = n.type === "link" && n.href ? parseLiveLink(n.href, appOrigin()) : null;
    if (liveLink) return <OpenInLive key={k} link={liveLink} label={plainText(n.content)} />;
    if (n.type === "link" && n.href && /^https?:\/\//.test(n.href))
      return (
        <a
          key={k}
          href={n.href}
          target="_blank"
          rel="noreferrer"
          className="text-primary underline"
        >
          {inline(n.content, `${k}-`)}
        </a>
      );
    if (n.type === "mention" && mention) {
      // The composer's mention ids are "person:<membership>" / "de:<membership>".
      const id = memberOfRef(n.props?.id);
      return <Fragment key={k}>{mention(n.props?.label ?? n.props?.name ?? "", id)}</Fragment>;
    }
    if (n.type === "mention")
      return (
        <span
          key={k}
          data-mention=""
          className="rounded-sm bg-primary-soft px-0.5 font-medium text-primary-text"
        >
          @{n.props?.label ?? n.props?.name ?? ""}
        </span>
      );
    const text = n.text ?? "";
    const s = n.styles ?? {};
    let node: ReactNode = text;
    if (s.code)
      node = <code className="rounded-sm bg-muted px-1 font-mono text-[0.9em]">{node}</code>;
    if (s.bold) node = <strong className="font-semibold">{node}</strong>;
    if (s.italic) node = <em>{node}</em>;
    if (s.strike) node = <s>{node}</s>;
    return <span key={k}>{node}</span>;
  });
}

/** Consecutive list items as one list. */
function group(blocks: Block[]): (Block | { list: "ul" | "ol"; items: Block[] })[] {
  const out: (Block | { list: "ul" | "ol"; items: Block[] })[] = [];
  for (const b of blocks) {
    const list = b.type === "bulletListItem" ? "ul" : b.type === "numberedListItem" ? "ol" : null;
    const last = out.at(-1);
    if (list && last && "list" in last && last.list === list) last.items.push(b);
    else if (list) out.push({ list, items: [b] });
    else out.push(b);
  }
  return out;
}

export function MessageBlocks({ body, mention }: { body: unknown; mention?: MentionRender }) {
  const blocks = (Array.isArray(body) ? body.filter(isObj) : []) as Block[];
  const run = (nodes: unknown, key: string) => inline(nodes, key, mention);
  // CHAT-DM: nested blocks (an indented paragraph or item) render beneath their parent.
  const nested = (b: Block) =>
    Array.isArray(b.children) && b.children.length ? (
      <div className="pl-4" data-slot="message-blocks-children">
        <MessageBlocks body={b.children} {...(mention ? { mention } : {})} />
      </div>
    ) : null;
  const grouped = group(blocks);
  return (
    <div className="flex flex-col gap-1.5 text-sm" data-slot="message-blocks">
      {grouped.map((b, i) =>
        "list" in b ? (
          one(b, i)
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: a message's blocks never reorder.
          <Fragment key={`b${i}`}>
            {one(b, i)}
            {nested(b)}
          </Fragment>
        ),
      )}
    </div>
  );
  function one(b: (typeof grouped)[number], i: number) {
    const key = `b${i}`;
    if ("list" in b) {
      const items = b.items.map((it, j) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a message's blocks never reorder.
        <li key={j}>
          {run(it.content, `${key}-${j}-`)}
          {nested(it)}
        </li>
      ));
      return b.list === "ul" ? (
        <ul key={key} className="list-disc pl-5">
          {items}
        </ul>
      ) : (
        <ol key={key} className="list-decimal pl-5">
          {items}
        </ol>
      );
    }
    if (b.type === "heading")
      // Inside a page whose own headings are h1/h2: a message's heading is an h3 or h4.
      return (b.props?.level ?? 1) <= 2 ? (
        <h3 key={key} className="text-base font-semibold">
          {run(b.content, `${key}-`)}
        </h3>
      ) : (
        <h4 key={key} className={cn("font-semibold")}>
          {run(b.content, `${key}-`)}
        </h4>
      );
    if (b.type === "quote")
      return (
        <blockquote key={key} className="border-l-2 border-border pl-3 text-muted-foreground">
          {run(b.content, `${key}-`)}
        </blockquote>
      );
    if (b.type === "codeBlock")
      return (
        <pre key={key} className="overflow-x-auto rounded-md bg-muted p-2 font-mono text-xs">
          {run(b.content, `${key}-`)}
        </pre>
      );
    return (
      <p key={key} className="whitespace-pre-wrap">
        {run(b.content, `${key}-`)}
      </p>
    );
  }
}
